import { cached } from '@porotutu/react'
import {
  historyDeps,
  historySearchOf,
  historyWindow,
} from '@porotutu/react/components/History/History'
import type { Writable } from '@porotutu/react/components/RecordTrail/RecordTrail'
import { listQueryOf } from '@porotutu/react/paging'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createRoute, lazyRouteComponent, useNavigate } from '@tanstack/react-router'
import { actsRoute } from '@/router/actsRoute'
import { permittedRoute, signedInRoute } from '@/router/root'
import { useSession } from '@/session'
import { ORGANIZATION_SORTABLE } from '../vocabulary'
import { OrganizationsList } from './components/List'
import {
  type Organization,
  organizationHistoryQuery,
  organizationQuery,
  organizationsQuery,
} from './queries'

const organizationSearch = (raw: Record<string, unknown>) => listQueryOf(raw, ORGANIZATION_SORTABLE)

type OrganizationSearch = ReturnType<typeof organizationSearch>

const historySearch = historySearchOf(organizationSearch)

export const organizationTrail = (
  id: string,
  search: OrganizationSearch,
  { writable }: Writable,
) => ({
  root: { to: '/organizations', search },
  show: { to: '/organizations/$id', params: { id }, search },
  edit: writable ? { to: '/organizations/$id/edit', params: { id }, search } : undefined,
  history: { to: '/organizations/$id/history', params: { id }, search },
})

export const organizationsRoute = createRoute({
  getParentRoute: () => signedInRoute,
  beforeLoad: permittedRoute('organizations'),
  path: '/organizations',
  validateSearch: organizationSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.query(cached(organizationsQuery(deps))),
  component: OrganizationsPage,
})

function OrganizationsPage() {
  const query = organizationsRoute.useSearch()
  const navigate = useNavigate({ from: organizationsRoute.fullPath })
  const { locale } = useSession()
  const { data: page } = useSuspenseQuery(organizationsQuery(query))

  return (
    <OrganizationsList
      page={page}
      query={query}
      locale={locale}
      onQuery={(patch) => navigate({ search: (prev) => ({ ...prev, ...patch }) })}
      onReset={() => navigate({ search: {} })}
    />
  )
}

export const organizationRoute = createRoute({
  getParentRoute: () => signedInRoute,
  beforeLoad: permittedRoute('organizations'),
  path: '/organizations/$id',
  validateSearch: organizationSearch,
  loader: ({ context, params }) => context.queryClient.query(cached(organizationQuery(params.id))),
  component: lazyRouteComponent(() => import('./pages'), 'OrganizationPage'),
})

export const editOrganizationRoute = createRoute({
  getParentRoute: () => signedInRoute,
  beforeLoad: actsRoute<Organization>(
    'organizations',
    'edit',
  )(({ id }: { id: string }) => organizationQuery(id)),
  path: '/organizations/$id/edit',
  validateSearch: organizationSearch,
  loader: ({ context, params }) => context.queryClient.query(cached(organizationQuery(params.id))),
  component: lazyRouteComponent(() => import('./pages'), 'EditOrganizationPage'),
})

export const organizationHistoryRoute = createRoute({
  getParentRoute: () => signedInRoute,
  beforeLoad: permittedRoute('organizations'),
  path: '/organizations/$id/history',
  validateSearch: historySearch,
  loaderDeps: historyDeps,
  loader: ({ context, params, deps }) =>
    Promise.all([
      context.queryClient.query(cached(organizationQuery(params.id))),
      context.queryClient.query(
        cached(organizationHistoryQuery(params.id, historyWindow(deps.history_offset))),
      ),
    ]),
  component: lazyRouteComponent(() => import('./pages'), 'OrganizationHistoryPage'),
})

export const organizationRoutes = [
  organizationsRoute,
  organizationRoute,
  editOrganizationRoute,
  organizationHistoryRoute,
]
